import { useEffect, useState } from 'react';

import { loadVGSCollect } from '@vgs/collect-js';
import useEnv from '@zenbusiness/application-commons-env';
import useIsMounted from '@zenbusiness/application-commons-hooks/useIsMounted';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import PropTypes from 'prop-types';
import states from 'states-us';

import useToastContext from '#client/hooks/useToastContext';

import { classes, GenerateInputCSS, cardIcon, GenerateSelectCSS } from '../components/VGS.styles';

import useBeneficialOwerAdress from './useBeneficialOwerAdress';

/**
 * Custom hook for initializing VGS Collect form.
 * @param {Function} handleClose - Function to handle closing the form.
 * @returns {object} - Object containing form, formState, street, city, state, postalCode, fullName, loading, isSubmitting, and handleSubmit.
 */
export default function useInitVgs(handleClose) {
  const captureException = useCaptureException();
  const { createCommonErrorToast } = useToastContext();

  const { loading, street, city, state, postalCode, fullName } = useBeneficialOwerAdress();
  const [form, setForm] = useState(null);
  const [formState, setFormState] = useState(null);

  const isMounted = useIsMounted();

  const {
    VGS_COLLECT_VERSION,
    VGS_COLLECT_ENV,
    VGS_COLLECT_ASTRA_VAULT_ID,
    VGS_COLLECT_ASTRA_CNAME
  } = useEnv();

  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const InputCSS = GenerateInputCSS(isMobile);
  const SelectCSS = GenerateSelectCSS(isMobile);

  const createForm = async () => {
    try {
      const collect = await loadVGSCollect({
        vaultId: VGS_COLLECT_ASTRA_VAULT_ID,
        environment: VGS_COLLECT_ENV,
        version: VGS_COLLECT_VERSION
      });

      const vgsForm = collect.init((status) => {
        setFormState(status);
      });

      vgsForm.useCname(VGS_COLLECT_ASTRA_CNAME);

      // Create VGS Collect field for credit card name
      vgsForm.field('#cc-first-name', {
        type: 'text',
        name: 'first_name',
        css: InputCSS,
        classes,
        maxLength: 40,
        placeholder: 'First Name',
        validations: ['required']
      });

      // Create VGS Collect field for credit card name
      vgsForm.field('#cc-last-name', {
        type: 'text',
        name: 'last_name',
        css: InputCSS,
        classes,
        maxLength: 40,
        placeholder: 'Last Name',
        validations: ['required']
      });

      // Create VGS Collect field for credit card number
      vgsForm.field('#cc-number', {
        type: 'card-number',
        name: 'card_number',
        iframeTitle: 'Card number',
        placeholder: 'Card number',
        inputMode: 'numeric',
        css: {
          ...InputCSS,
          boxSizing: 'border-box',
          paddingLeft: '42px',
          paddingRight: '0'
        },
        classes,
        validCardBrands: [{ type: 'visa' }, { type: 'mastercard' }],
        showCardIcon: {
          top: isMobile ? '28px' : '32px',
          transform: 'translateY(-50%)',
          left: '14px',
          width: '24px',
          height: 'auto'
        },
        icons: {
          cardPlaceholder: cardIcon
        },
        validations: ['required', 'validCardNumber'],
        // Reference: https://www.verygoodsecurity.com/docs/vgs-collect/js/customization
        addCardBrands: [
          {
            type: 'visa',
            pattern: /^4/,
            format: /(\d{1,4})/g,
            length: [16],
            cvcLength: [3],
            luhn: true
          }
        ]
      });

      // Create VGS Collect field for credit card expiration date
      vgsForm.field('#cc-expiration-date', {
        type: 'card-expiration-date',
        name: 'expiration_date',
        inputMode: 'numeric',
        placeholder: 'MM / YY',
        yearLength: 2,
        css: {
          ...InputCSS,
          padding: 0
        },
        classes,
        validations: ['required', 'validCardExpirationDate']
      });

      // Create VGS Collect field for CVC
      vgsForm.field('#cc-cvc', {
        type: 'card-security-code',
        name: 'card_security_code',
        inputMode: 'numeric',
        placeholder: 'CVC',
        css: {
          ...InputCSS,
          padding: 0
        },
        classes,
        maxLength: 3,
        validations: ['required', 'validCardSecurityCode']
      });

      vgsForm.field('#street_line_1', {
        type: 'text',
        name: 'street_line_1',
        css: InputCSS,
        classes,
        placeholder: 'Address',
        defaultValue: street,
        validations: ['required']
      });

      vgsForm.field('#city', {
        type: 'text',
        name: 'city',
        css: InputCSS,
        classes,
        placeholder: 'City',
        defaultValue: city,
        validations: ['required']
      });

      vgsForm.field('#state', {
        type: 'dropdown',
        name: 'state',
        css: SelectCSS,
        classes,
        placeholder: 'State',
        defaultValue: state,
        options: states.map((st) => ({
          value: st.abbreviation,
          text: st.name
        })),
        validations: ['required']
      });

      vgsForm.field('#zip_code', {
        type: 'zip-code',
        name: 'zip_code',
        css: InputCSS,
        defaultValue: postalCode,
        classes,
        placeholder: 'Zip code',
        validations: ['required']
      });

      vgsForm.field('#added_by_user', {
        type: 'checkbox',
        name: 'added_by_user',
        defaultValue: true,
        placeholder: 'Add by user'
      });

      setForm(vgsForm);
    } catch (e) {
      createCommonErrorToast();
      handleClose();
      captureException(e, {
        level: 'warning'
      });
    }
  };

  useEffect(() => {
    if (VGS_COLLECT_VERSION && isMounted() && !loading) {
      createForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VGS_COLLECT_VERSION, loading]);

  return {
    form,
    formState,
    street,
    city,
    state,
    postalCode,
    fullName,
    loading
  };
}

useInitVgs.propTypes = {
  handleClose: PropTypes.func.isRequired
};
